/**
 * Replay d'ouverture (demande utilisateur 13/06/2026) : à l'ouverture du
 * dashboard, on rejoue les résultats DÉJÀ JOUÉS en mode simulation — logique
 * du legacy — d'abord la phase de groupes (classements animés en FLIP, dans
 * l'ordre des matchs FIFA), puis le tableau final APPARAÎT quand on atteint
 * les 16es, et enfin on bascule en direct une fois rattrapé le présent.
 *
 * Une seule exécution, à l'ouverture. Contrôles : pause, vitesse x1–x8,
 * « Aller au direct ». Respecte prefers-reduced-motion (rendu direct, sans
 * animation).
 */
import type { OfficialState } from "../app/officialStore.ts";
import { groups, initialElos } from "../app/data.ts";
import { rankGroup } from "../engine/standings.ts";
import type { EloTable, GroupMatchResult } from "../engine/types.ts";
import { flipGroup, setGroupHeaderPlayed, morphGroupTo } from "./components/groupsGrid.ts";
import { flagSpan, roundName } from "./html.ts";
import { fmtShortDateTime } from "../app/format.ts";
import { t } from "../app/i18n.ts";

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
const SPEEDS = [1, 2, 4, 8];

// Cadence ADAPTATIVE : le replay vise une durée totale ~constante quel que soit
// le nombre de matchs déjà joués (4 en début de tournoi, 60+ en fin) — sinon
// ce serait interminable. On répartit un budget sur le nombre de matchs, borné
// pour rester regardable. Les boutons x1–x8 accélèrent encore par-dessus.
const TARGET_TOTAL_MS = 8000;
const MIN_PER_MATCH_MS = 150;
const MAX_PER_MATCH_MS = 1000;

/** Surcharge de cadence (le mode Simulation démarre plus lentement). */
export interface ReplayPace {
  readonly targetTotalMs?: number;
  readonly minPerMatchMs?: number;
  readonly maxPerMatchMs?: number;
  /** Pause sur les classements FINALS des poules avant le passage au tableau
   *  (ms). 0 = aucune pause (défaut, dashboard). La Simulation en met une. */
  readonly koPauseMs?: number;
}

export function paceForCount(
  count: number,
  pace: ReplayPace = {},
): { preMs: number; holdMs: number; perMatchMs: number } {
  const target = pace.targetTotalMs ?? TARGET_TOTAL_MS;
  const min = pace.minPerMatchMs ?? MIN_PER_MATCH_MS;
  const max = pace.maxPerMatchMs ?? MAX_PER_MATCH_MS;
  const per = Math.min(max, Math.max(min, target / Math.max(1, count)));
  return { preMs: Math.round(per * 0.55), holdMs: Math.round(per * 0.45), perMatchMs: per };
}

export interface ReplayHandle {
  cancel(): void;
  readonly done: Promise<void>;
}

interface ReplayOptions {
  readonly official: OfficialState;
  readonly scoreboardEl: HTMLElement;
  readonly groupsEl: HTMLElement;
  /** Called once, when the replay first reaches a knockout match. */
  readonly onReachKO: () => void;
  /** Called when the replay finishes or is skipped — switch to live mode. */
  readonly onComplete: () => void;
  /** Force the replay even under prefers-reduced-motion (manual « Rejouer »). */
  readonly force?: boolean;
  /** Elo table for the FLIP tie-break ranking (default: real initialElos). The
   *  Simulation mode passes the scenario's model so intermediate standings match. */
  readonly elos?: EloTable;
  /** Draw-seed prefix for the FLIP ranking (default "wc26-replay"). */
  readonly flipSeedPrefix?: string;
  /** Cadence override (the Simulation mode plays slower initially). */
  readonly pace?: ReplayPace;
  /** Si vrai, on ATTEND un clic utilisateur (« Passer au tableau ») avant la
   *  transition KO, au lieu d'une pause minutée. Utilisé par la Simulation. */
  readonly koClickToContinue?: boolean;
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export function runOpeningReplay(opts: ReplayOptions): ReplayHandle {
  const { official, scoreboardEl, groupsEl, onReachKO, onComplete } = opts;
  let cancelled = false;
  let paused = false;
  // La cadence de base est adaptée au nombre de matchs ; le multiplicateur
  // manuel démarre à x1 (les boutons permettent d'accélérer davantage).
  let speed = 1;
  const { preMs, holdMs } = paceForCount(official.finished.length, opts.pace);
  const koPauseMs = opts.pace?.koPauseMs ?? 0;
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((res) => (resolveDone = res));

  const finish = (): void => {
    if (cancelled) return;
    cancelled = true;
    scoreboardEl.classList.add("hide");
    scoreboardEl.innerHTML = "";
    onComplete();
    resolveDone();
  };

  // Rien à rejouer, ou mouvement réduit (sauf relecture forcée) : direct au live.
  if (official.finished.length === 0 || (RM && !opts.force)) {
    queueMicrotask(finish);
    return { cancel: finish, done };
  }

  scoreboardEl.classList.remove("hide");
  scoreboardEl.innerHTML = `
    <div class="replay">
      <div class="rp-top"><span class="dot"></span><span id="rpPhase">${t("replay.resuming")}</span><span id="rpProgress" class="dim"></span></div>
      <div class="rp-main">
        <div class="rp-team" id="rpA"><span class="ph"></span></div>
        <div class="rp-score" id="rpScore">–</div>
        <div class="rp-team away" id="rpB"><span class="ph"></span></div>
      </div>
      <div class="rp-ctrls">
        <button id="rpPlay" title="${t("replay.pauseResumeTitle")}" aria-label="${t("replay.pauseAria")}"><i class="ti ti-player-pause" aria-hidden="true"></i></button>
        <button id="rpSpeed" title="${t("replay.speedTitle")}">x1</button>
        <button id="rpSkip" class="primary">${t("replay.skipToLive")} <i class="ti ti-player-skip-forward"></i></button>
      </div>
    </div>`;

  const $ = (id: string): HTMLElement | null => scoreboardEl.querySelector(`#${id}`);
  const playBtn = $("rpPlay");
  const speedBtn = $("rpSpeed");
  if (playBtn) {
    playBtn.onclick = (): void => {
      paused = !paused;
      playBtn.innerHTML = `<i class="ti ti-player-${paused ? "play" : "pause"}" aria-hidden="true"></i>`;
      playBtn.setAttribute("aria-label", paused ? t("replay.resumeAria") : t("replay.pauseAria"));
    };
  }
  if (speedBtn) {
    speedBtn.onclick = (): void => {
      speed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length] ?? 1;
      speedBtn.textContent = `x${speed}`;
    };
  }
  const skipBtn = $("rpSkip");
  if (skipBtn) skipBtn.onclick = finish;

  /** Wait `ms` (scaled by speed), honoring pause and cancellation. */
  const beat = async (ms: number): Promise<void> => {
    let left = ms / speed;
    while (left > 0) {
      if (cancelled) throw new Error("cancelled");
      await sleep(40);
      if (!paused) left -= 40;
    }
  };

  /** Porte « passer au tableau » : remplace le scoreboard par un bouton et
   *  attend le clic utilisateur (ou l'annulation), puis restaure le scoreboard. */
  const koGate = (): Promise<void> =>
    new Promise<void>((resolve) => {
      const main = scoreboardEl.querySelector<HTMLElement>(".rp-main");
      const original = main?.innerHTML ?? "";
      if (main) {
        main.innerHTML = `<button id="rpKoGo" class="primary rp-kogo">${t("replay.toBracket")} <i class="ti ti-arrow-right" aria-hidden="true"></i></button>`;
      }
      let iv = 0;
      const settle = (): void => {
        if (iv) window.clearInterval(iv);
        if (main) main.innerHTML = original;
        resolve();
      };
      const go = scoreboardEl.querySelector<HTMLButtonElement>("#rpKoGo");
      if (go) go.onclick = settle;
      iv = window.setInterval(() => {
        if (cancelled) settle();
      }, 80);
    });

  const groupResults: Record<string, GroupMatchResult[]> = {};
  let koRevealed = false;
  const total = official.finished.length;

  const run = async (): Promise<void> => {
    try {
      for (let i = 0; i < official.finished.length; i++) {
        const fm = official.finished[i];
        if (fm === undefined) continue;
        const view = official.byId.get(fm.id);
        const isGroup = view?.match.stage === "group";
        // Avant le PREMIER match KO : on tient sur les classements finals des
        // poules, PUIS on déclenche le passage au tableau. La Simulation attend
        // un CLIC (« Passer au tableau ») ; sinon, une pause minutée optionnelle.
        if (!isGroup && !koRevealed) {
          koRevealed = true;
          if (opts.koClickToContinue) {
            const ph = $("rpPhase");
            if (ph) ph.textContent = t("replay.groupPhaseDone");
            await koGate();
            if (cancelled) throw new Error("cancelled");
          } else if (koPauseMs > 0) {
            const ph = $("rpPhase");
            if (ph) ph.textContent = t("replay.groupPhaseDoneToBracket");
            const sc = $("rpScore");
            if (sc) sc.textContent = "";
            for (const id of ["rpA", "rpB"]) {
              const el = $(id);
              if (el) el.innerHTML = "";
            }
            await beat(koPauseMs);
          }
          onReachKO();
          await beat(Math.max(holdMs, 350));
        }
        const phase = isGroup
          ? t("lf.groupMatchdayAbbr", { group: view?.match.group ?? "?", matchday: view?.match.matchday ?? "?" })
          : roundName(view?.match.stage ?? "");
        const progress = $("rpProgress");
        if (progress)
          progress.textContent = `${i + 1}/${total} · ${view ? fmtShortDateTime(view.match.utcDateTime) : ""}`;
        const phaseEl = $("rpPhase");
        if (phaseEl) phaseEl.textContent = phase;
        const aEl = $("rpA");
        const bEl = $("rpB");
        if (aEl) aEl.innerHTML = flagSpan(fm.home, { name: true });
        if (bEl) bEl.innerHTML = flagSpan(fm.away, { name: true });
        const scoreEl = $("rpScore");
        if (scoreEl) scoreEl.textContent = "–";

        await beat(preMs);
        if (scoreEl) {
          const pens = fm.penalties ? ` (${t("common.penaltiesAbbr")} ${view?.penalties?.score.join("-") ?? ""})` : "";
          scoreEl.textContent = `${fm.score[0]} – ${fm.score[1]}`;
          scoreEl.classList.remove("pop");
          void scoreEl.offsetWidth;
          scoreEl.classList.add("pop");
          if (pens && phaseEl) phaseEl.textContent = `${phase} · ${t("common.penaltiesAbbr")}`;
        }

        if (isGroup) {
          const g = view?.match.group;
          if (g) {
            (groupResults[g] ??= []).push({ home: fm.home, away: fm.away, score: fm.score });
            const ranked = rankGroup(groups[g] ?? [], groupResults[g], {
              elos: opts.elos ?? initialElos,
              drawSeed: `${opts.flipSeedPrefix ?? "wc26-replay"}:${g}`,
            });
            flipGroup(groupsEl, g, ranked, [fm.home, fm.away]);
            setGroupHeaderPlayed(groupsEl, g, groupResults[g].length);
          }
        }
        await beat(holdMs);
      }
      finish();
    } catch {
      /* cancelled — finish() already handled state */
    }
  };
  void run();

  return { cancel: finish, done };
}

// --- Intro d'ouverture du DASHBOARD : « déplacement fondu » ----------------
// Demande utilisateur (23/06/2026, affinée) : à l'ouverture, PLUS de scoreboard
// ni de score, et PAS toutes les étapes — on montre brièvement le squelette des
// groupes puis on MORPH chaque groupe DIRECTEMENT vers la situation ACTUELLE du
// classement, en un seul déplacement FONDU (les lignes glissent vers leur place
// pendant que le bloc s'éclaircit) ; ensuite le reste glisse et le live apparaît
// (cf. dashboard.revealAfterIntro). Le mode Simulation garde, lui, le replay
// détaillé `runOpeningReplay` ci-dessus (les scores y sont le sujet).
const INTRO_SETTLE_MS = 420; // on laisse percevoir l'état de départ (squelette)
const INTRO_MORPH_MS = 750; // temps laissé au morph avant la révélation

export interface GroupsIntroOptions {
  readonly official: OfficialState;
  /** Section d'en-tête (réservée — restée masquée : l'intro n'a pas de bandeau). */
  readonly headerEl: HTMLElement;
  readonly groupsEl: HTMLElement;
  readonly onComplete: () => void;
  /** Force l'animation même sous prefers-reduced-motion (bouton « Rejouer »). */
  readonly force?: boolean;
}

export function runGroupsIntro(opts: GroupsIntroOptions): ReplayHandle {
  const { official, headerEl, groupsEl, onComplete } = opts;
  let cancelled = false;
  let resolveDone: () => void = () => {};
  const done = new Promise<void>((res) => (resolveDone = res));

  const finish = (): void => {
    if (cancelled) return;
    cancelled = true;
    onComplete();
    resolveDone();
  };

  // Rien à montrer (aucun match de poule joué) ou mouvement réduit non forcé.
  const hasGroupResult = official.finished.some(
    (fm) => official.byId.get(fm.id)?.match.stage === "group",
  );
  if (!hasGroupResult || (RM && !opts.force)) {
    queueMicrotask(finish);
    return { cancel: finish, done };
  }

  headerEl.classList.add("hide"); // pas de bandeau : morph direct, sans étapes
  headerEl.innerHTML = "";
  const gzone = groupsEl.querySelector<HTMLElement>(".gzone");

  const run = async (): Promise<void> => {
    try {
      if (gzone) gzone.style.opacity = "0.55"; // squelette légèrement estompé
      await sleep(INTRO_SETTLE_MS);
      if (cancelled) return;
      // FONDU : le bloc s'éclaircit pendant que chaque groupe se réordonne vers
      // le classement ACTUEL (déplacement FLIP des lignes) — pas les étapes.
      if (gzone) {
        gzone.style.transition = "opacity .6s ease";
        gzone.style.opacity = "1";
      }
      for (const s of official.standings) {
        if (s.rows.length) morphGroupTo(groupsEl, s.group, s.rows);
      }
      await sleep(INTRO_MORPH_MS);
      if (gzone) {
        gzone.style.transition = "";
        gzone.style.opacity = "";
      }
      finish();
    } catch {
      /* annulé */
    }
  };
  void run();

  return { cancel: finish, done };
}
